import { useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { TextInput } from "../shared/Inputs/TextInput";
import Modal, { ModalProps } from "../../layouts/Modal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ContactCreateEditRequest, IContact } from "../../models/Contact";
import useNotification from "../notifications/useNotifications";
import useContactsMutations from "../../data/useContactsMutations";
import PickAddress from "../shared/Addresses/PickAddress";
import PossibleContactMatches from "./PossibleContactMatches";
import { canBeRecognised } from "../../utils/contactDuplicates";

interface ContactCreateEditModalProps
  extends Omit<ModalProps, "children" | "width"> {
  contact?: IContact;
  newContactName?: string;
  onSaved?: (contact?: IContact) => void;
}

export function ContactCreateEditModal({
  open,
  setOpen,
  contact,
  newContactName,
  onSaved,
}: ContactCreateEditModalProps) {
  const { t } = useTranslation("accounts");
  const { register, handleSubmit, reset, setValue, watch } = useForm<ContactCreateEditRequest>();
  const { create: createContact, update: updateContact } = useContactsMutations();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Contact"));
  const { addNotification } = useNotification();
  const watchEmail = watch("email");
  const watchTelephone1 = watch("telephone1");
  const watchTelephone2 = watch("telephone2");

  const onSubmit = (data: ContactCreateEditRequest) => {
    console.log(data);
    if (contact) {
      updateContact.mutate({ ...contact, ...data },
        {
          ...saveHandlers,
          onSuccess: (res: any) => {
            saveHandlers.onSuccess();
            onSaved?.(res?.data);
            setOpen(false);
          },
        }
      );
    } else {
      if (data.fullname === "") {
        addNotification({
          variant: "warn",
          primaryText: t("Please enter a contact name"),
        });
        return;
      }

      if (!canBeRecognised(data.email, data.telephone1, data.telephone2)) {
        addNotification({
          variant: "warn",
          primaryText: t("Add an email address or a phone number, so this person is recognised next time."),
        });
        return;
      }

      createContact.mutate({ ...data },
        {
          ...saveHandlers,
          onSuccess: (res: any) => {
            saveHandlers.onSuccess();
            onSaved?.(res?.data);
            setOpen(false);
          },
        }
      );
    }
  };

  useEffect(() => {
    if (contact) {
      reset({ ...contact });
    } else {
      reset({
        fullname: newContactName,
        contactType: 0,
      });
    }
  }, [contact, open, reset]);

  return (
    <Modal width="max-w-4xl" open={open} setOpen={setOpen}>
      <h2 className="text-xl font-semibold mb-6">{contact ? t("Edit Contact") : t("Add Contact")}</h2>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        <TextInput label={t("Full Name")} register={register} registerName="fullname" rounded />
        <TextInput label={t("Email Address")} register={register} registerName="email" rounded />

        <TextInput label={t("Primary Telephone")} register={register} registerName="telephone1" rounded />
        <TextInput label={t("Secondary Telephone")} register={register} registerName="telephone2" rounded />

        <PossibleContactMatches
          email={watchEmail}
          phone={watchTelephone1 || watchTelephone2}
          excludeContactId={contact?.id}
          onUse={
            contact
              ? undefined
              : (match) => {
                  // The existing person goes back to the caller exactly as a newly created one would.
                  onSaved?.(match);
                  setOpen(false);
                }
          }
        />

        <PickAddress classes="col-span-2" register={register} setValue={setValue} watch={watch} rounded />

        <ButtonInput
          label={t("Save")}
          isSubmit={true}
          classes="mt-2"
          onClick={undefined}
        />
      </form>
    </Modal>
  );
}
